import type { ProjectPhase, ProjectStatus } from '../../lib/api/domains/projects';

export const PHASES: ProjectPhase[] = ['BEFORE', 'DURING', 'AFTER'];

export const PHASE_LABEL: Record<ProjectPhase, string> = {
  BEFORE: 'Vorher',
  DURING: 'Währenddessen',
  AFTER: 'Nachher',
};

/** Untertitel der Phase — was dort hingehört. */
export const PHASE_HINT: Record<ProjectPhase, string> = {
  BEFORE: 'Vorbereitung und Ist-Zustand, bevor etwas angefasst wird',
  DURING: 'Durchführung vor Ort',
  AFTER: 'Abschluss, Übergabe und Nachkontrolle',
};

export const PHASE_COLOR: Record<ProjectPhase, string> = {
  BEFORE: 'grape',
  DURING: 'blue',
  AFTER: 'teal',
};

export const STATUS_LABEL: Record<ProjectStatus, string> = {
  PLANNED: 'Geplant',
  IN_PROGRESS: 'In Arbeit',
  ON_HOLD: 'Pausiert',
  DONE: 'Abgeschlossen',
  CANCELLED: 'Abgebrochen',
};

export const STATUS_COLOR: Record<ProjectStatus, string> = {
  PLANNED: 'gray',
  IN_PROGRESS: 'blue',
  ON_HOLD: 'yellow',
  DONE: 'green',
  CANCELLED: 'red',
};

export const STATUS_OPTIONS = (Object.keys(STATUS_LABEL) as ProjectStatus[]).map((value) => ({
  value,
  label: STATUS_LABEL[value],
}));

/**
 * 'JJJJ-MM-TT' → '05.10.2026', ohne Umweg über `new Date()` — ein Tagesdatum hat keine
 * Uhrzeit, und über UTC gelesen rutscht es je nach Zeitzone auf den Vortag.
 */
export function formatDay(ymd: string | null | undefined): string {
  if (!ymd) return '–';
  const [y, m, d] = ymd.split('-');
  return y && m && d ? `${d}.${m}.${y}` : ymd;
}

/**
 * Getipptes Datum „8.1.2027" / „08.01.2027" → '2027-01-08' — für `dateParser` der DateInputs.
 * Ohne ihn liest Mantine „08.01.2027" als 1. August (US-Reihenfolge).
 */
export function parseGermanDate(input: string): string | null {
  const match = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(input.trim());
  if (!match) return null;
  const [, day, month, year] = match;
  const iso = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  const date = new Date(`${iso}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso ? null : iso;
}

/** Heute in Berlin als 'JJJJ-MM-TT' — Vergleichsbasis für „überfällig". */
export function todayYmd(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(new Date());
}

/**
 * „3 Tage vor Start", „am Starttag", „2 Tage nach Praxisstart" — relative Fälligkeit aus einer
 * Vorlage. `ref` ist der Name des Bezugstags (Tag 0 oder zweiter Stichtag).
 */
export function offsetLabel(days: number | null | undefined, ref = 'Start'): string | null {
  if (days == null) return null;
  if (days === 0) return ref === 'Start' ? 'am Starttag' : `am ${ref}`;
  const abs = Math.abs(days);
  return `${abs} Tag${abs === 1 ? '' : 'e'} ${days < 0 ? 'vor' : 'nach'} ${ref}`;
}

/** Name des Bezugstags einer Aufgabe: Tag 0 (z. B. „Umstellungsstart") oder zweiter Stichtag („Praxisstart"). */
export function anchorName(
  anchor: 'START' | 'GO_LIVE' | null | undefined,
  labels: { startLabel?: string | null; goLiveLabel?: string | null },
): string {
  return anchor === 'GO_LIVE' ? labels.goLiveLabel?.trim() || 'Stichtag 2' : labels.startLabel?.trim() || 'Start';
}

/** 'JJJJ-MM-TT' + n Tage (ohne Zeitzonen-Umweg). */
export function addDaysYmd(ymd: string, days: number): string {
  return new Date(ymdToUtc(ymd) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Ganze Tage von a nach b. */
export function daysBetweenYmd(a: string, b: string): number {
  return Math.round((ymdToUtc(b) - ymdToUtc(a)) / 86_400_000);
}

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** „Fr, 15.01.2027" — beim Praxisstart zählt der Wochentag. */
export function formatDayWithWeekday(ymd: string | null | undefined): string {
  if (!ymd) return '–';
  return `${WEEKDAYS[new Date(ymdToUtc(ymd)).getUTCDay()]}, ${formatDay(ymd)}`;
}

function ymdToUtc(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/**
 * Plan-Ende nach verschobenem Start: gleiche Dauer wie vorher (wie der Server,
 * `shiftedEndDate`). Ohne alten Start oder Ende bleibt das Ende, wie es ist.
 */
export function shiftEndWithStart(oldStart: string | null, oldEnd: string | null, newStart: string | null): string | null {
  if (!oldStart || !oldEnd || !newStart) return oldEnd;
  const shifted = ymdToUtc(oldEnd) + (ymdToUtc(newStart) - ymdToUtc(oldStart));
  return new Date(shifted).toISOString().slice(0, 10);
}
